/**
=========================================================
* Material Dashboard 2 PRO React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { AuthContext } from "context";

// @material-ui core components
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";

import questions from "./questions";

// Settings page components
import CrudService from "services/cruds-service";
import { useState, useEffect, useContext } from "react";
import DataTable from "examples/Tables/DataTable";
import { Alert, CircularProgress, Snackbar } from "@mui/material";
import { useMaterialUIController, setOpenChatBot } from "context";
import Question from "../Assessment/question";
import MDInput from "components/MDInput";
import crudsService from "services/cruds-service";
import MDAlert from "components/MDAlert";

function CourseFeedback({ courseId, courseName }) {
  const [controller, dispatch] = useMaterialUIController();
  useEffect(() => {
    setOpenChatBot(dispatch, false);
    setFeedbackQuestions(questions);
  }, []);
  const [user, setUser] = useState({});
  const { setIsAuthenticated, getCurrentUser } = useContext(AuthContext);
  const [notification, setNotification] = useState({
    value: false,
    color: "info",
    message: "",
  });
  const [feedbackQuestions, setFeedbackQuestions] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answeredQuestions, setAnsweredQuestions] = useState([]);
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);

  useEffect(() => {
    async function checkToken() {
      try {
        let user_id = await getCurrentUser();
        if (!user_id) {
          setIsAuthenticated(false);
          localStorage.removeItem("token");
        }
        return user_id;
      } catch (error) {
        console.log(error);
        alert(
          "Something went wrong. Please refresh the page or try again later."
        );
      }
    }

    checkToken().then((user_id) => {
      setUser(user_id);
    });
  }, []);

  const submitFeedbackResults = async () => {
    // check if all the questions are empty, if so, don't submit
    if (answeredQuestions.length === 0) {
      setNotification({
        value: true,
        color: "error",
        message: "Please provide feedback before submitting.",
      });
      return;
    }
    try {
      const response = await crudsService.postFeedback({
        course_id: courseId,
        user_id: user,
        feedback: answeredQuestions,
      });
      setNotification({
        value: true,
        color: response.status === 200 ? "success" : "error",
        message: "Thank you for submitting your feedback!",
      });
    } catch (error) {
      console.log(error);
      setNotification({
        value: true,
        color: "error",
        message: "Something went wrong. Please try again later.",
      });
    }
  };

  useEffect(() => {
    if (notification.value === true) {
      let timer = setTimeout(() => {
        setNotification({ value: false, color: "info", message: "" });
      }, 5000);
    }
  }, [notification]);

  return (
    <Card id="feedback">
      {notification.value === true && (
        <Snackbar open>
          <Alert variant="filled" sx={{ width: "100%" }} color="success">
            {notification.message}
          </Alert>
        </Snackbar>
      )}
      <MDTypography variant="h2" mt={3} mx={4}>
        {courseName}
      </MDTypography>
      <MDBox
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        flexDirection={{ xs: "column", sm: "row" }}
        mt={3}
      >
        {/* Display the menu to navigate through the questions */}
        {feedbackQuestions.length > 0 && (
          <>
            <MDBox
              display="flex"
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
              p={3}
              borderLeft="1px solid"
              borderColor="divider"
            >
              <MDBox mb={3} mx={2}>
                <MDTypography variant="h6">
                  Feedback Questions Menu
                </MDTypography>
              </MDBox>
              <Grid container spacing={1} justifyContent="center">
                {feedbackQuestions.map((question, index) => (
                  <Grid item xs={3} sm={1} key={index}>
                    <MDButton
                      key={index}
                      variant={
                        currentQuestion === index ? "contained" : "outlined"
                      }
                      color="info"
                      fullWidth
                      onClick={() => setCurrentQuestion(index)}
                    >
                      {index + 1}
                    </MDButton>
                  </Grid>
                ))}
              </Grid>
            </MDBox>
            <MDBox mx={3}>
              <MDTypography variant="h5" mb={3}>
                {currentQuestion + 1}. {feedbackQuestions[currentQuestion]}
              </MDTypography>

              <MDInput
                fullWidth
                multiline
                rows={4}
                placeholder="Your feedback here..."
                value={answeredQuestions[currentQuestion]?.user_answer || ""}
                onChange={(e) => {
                  let temp = answeredQuestions;
                  temp[currentQuestion] = {
                    question: feedbackQuestions[currentQuestion],
                    user_answer: e.target.value,
                  };
                  setAnsweredQuestions([...temp]);
                }}
                style={{
                  marginBottom: "20px",
                }}
              />
              <MDButton
                variant="contained"
                color="info"
                disabled={feedbackSubmitted}
                onClick={() => {
                  setCurrentQuestion(
                    (currentQuestion + 1) % feedbackQuestions.length
                  );
                }}
              >
                Next
              </MDButton>
            </MDBox>

            <MDBox display="flex" justifyContent="center" my={3}>
              <MDButton
                variant="contained"
                color="info"
                onClick={submitFeedbackResults}
              >
                Submit Feedback
              </MDButton>
            </MDBox>
          </>
        )}
      </MDBox>
    </Card>
  );
}

export default CourseFeedback;
